<template>
  <div ref="editModal" :class="componentClass" tabindex="1" @click.stop @blur="handleBlur">
    <div class="edit-modal-content">
      <ellipsis-text :text="textContentLocal" />
      <span class="icon" @click.stop="handleEdit">
        <svg-icon :name="editorIconName" kind="action" />
      </span>
      <span class="icon" @click.stop="handleDelete">
        <svg-icon :name="deleteIconName" kind="action" />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CLASS } from "@plugins/dynamic-link/dynamic.link.constant";
import { createPopper, Instance as PopperInstance } from "@popperjs/core";
import dynamicLinkEventBus from "@plugins/dynamic-link/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { ref, computed, useTemplateRef, nextTick, onBeforeUnmount } from "vue";

defineOptions({ inheritAttrs: false });

// Props
const props = defineProps({
  textContent: {
    type: String,
    default: "",
    required: false,
  },
  editorIconName: {
    type: String,
    default: "Pencil16",
    required: false,
  },
  deleteIconName: {
    type: String,
    default: "Unlink16",
    required: false,
  },
  isShown: {
    type: Boolean,
    default: false,
    required: false,
  },
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_parentComponent: { type: Object, default: null },
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_isDynamicallyMounted: { type: Boolean, default: false },
});

useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const editModal = useTemplateRef("editModal");

// Data
const popperInstance = ref<PopperInstance | null>(null);
const eventWatchInstance = ref({});
const textContentLocal = ref(props.textContent);
const isShownLocal = ref(props.isShown);

// Computed
const componentClass = computed(() => ({
  [CLASS.DYNAMIC_LINK_EDIT_MODAL_WRAPPER]: true,
  "hidden-modal": !isShownLocal.value,
}));

// Methods
const handleBlur = () => {
  hide();
};

const handleEdit = () => {
  dynamicLinkEventBus.$emit("edit");
};

const handleDelete = () => {
  dynamicLinkEventBus.$emit("delete");
};

const showAt = async (
  element: HTMLElement,
  popperOptions = {},
  allowedAutoPlacements = ["bottom"],
) => {
  isShownLocal.value = true;
  if (element && editModal.value) {
    popperOptions = Object.assign({}, popperOptions, { placement: "auto" });
    const popperConfigs = popperOptions.modifiers ?? [];
    popperConfigs.push({
      name: "flip",
      options: { allowedAutoPlacements },
    });

    popperOptions.modifiers = popperConfigs;

    popperInstance.value = createPopper(element, editModal.value, {
      placement: "auto",
      ...popperOptions,
      popperConfigs,
    });

    await nextTick();
    editModal.value?.focus();
  }
};

const hide = () => {
  isShownLocal.value = false;
};

const setModalContent = (message: string) => {
  textContentLocal.value = message;
};

const setEventListener = (event, callback) => {
  if (eventWatchInstance.value[event]) {
    eventWatchInstance.value[event]();
  }
  eventWatchInstance.value[event] = dynamicLinkEventBus.$on(event, callback);
};

defineExpose({
  showAt,
  hide,
  setModalContent,
  setEventListener,
});

onBeforeUnmount(() => {
  popperInstance.value?.destroy();
  EventBusUtils.unWatchInstance(Object.values(eventWatchInstance.value));
});
</script>

<style lang="scss">
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.sls-dynamic-link-edit-modal-wrapper {
  max-width: 20rem;
  background-color: $ui-01;
  margin-top: $spacing-04 !important;
  margin-left: $spacing-08 !important;
  padding: $spacing-03;
  border-radius: 10px;
  box-shadow: 0 0 4px 1px rgba($ui-05, 0.3);
  z-index: z-map("default");

  .edit-modal-content {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  .bx--text-truncate--end {
    color: $interactive-01;
  }
  .icon {
    max-height: 16px;
    margin-left: $spacing-05;
  }

  &.hidden-modal {
    display: none;
  }
}
</style>
